import { useEffect, useMemo, useState } from "react";
import {
  investors as investorApi,
  quantfolio,
  type InboxAction,
  type InboxItem,
  type InboxStatus,
} from "../api";
import { Link } from "../router";
import { formatDateTime } from "./crm/format";
import { qpRail } from "../lib/qpRail";

type StatusFilter = "new" | "done" | "all";
type KindFilter = "all" | "account" | "order" | "savings_plan";

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "new", label: "New" },
  { key: "done", label: "Processed" },
  { key: "all", label: "All" },
];
const KIND_FILTERS: { key: KindFilter; label: string }[] = [
  { key: "all", label: "All kinds" },
  { key: "account", label: "Accounts" },
  { key: "order", label: "Orders" },
  { key: "savings_plan", label: "Savings plans" },
];

// `savings_plan` reads badly in a pill.
const KIND_LABEL: Record<InboxItem["kind"], string> = {
  account: "account",
  order: "order",
  savings_plan: "savings plan",
};

// New + failed items can still be actioned; done/ignored are terminal.
const isActionable = (it: InboxItem) => it.status === "new" || it.status === "failed";

interface Group {
  key: string;
  investorId: number | null;
  name: string;
  items: InboxItem[];
}

// The Quantfolio inbox: order & account-opening requests from QP, grouped per
// client. Process one item, or bulk-apply a status to every selected item
// (e.g. execute all of a client's pending orders).
export function Inbox() {
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("new");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [clientFilter, setClientFilter] = useState<number | "all">("all");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [names, setNames] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | "sync" | "bulk" | null>(null);
  const [menu, setMenu] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function load() {
    quantfolio
      .inbox(statusFilter === "all" ? undefined : (statusFilter as InboxStatus))
      .then((d) => {
        setItems(d.items);
        setSelected(new Set());
      })
      .catch((e) => setError((e as Error).message));
  }
  useEffect(load, [statusFilter]);

  // Investor names for group headers + the client filter.
  useEffect(() => {
    investorApi
      .list()
      .then((d) => {
        const m: Record<number, string> = {};
        for (const inv of d.investors) m[inv.id] = inv.name;
        setNames(m);
      })
      .catch(() => {});
  }, []);

  const clientName = (id: number | null) =>
    id === null ? "Unlinked client" : (names[id] ?? `Investor #${id}`);

  const clientOptions = useMemo(() => {
    const ids = new Set<number>();
    for (const it of items ?? []) if (it.investor_id) ids.add(it.investor_id);
    return [...ids].sort((a, b) => clientName(a).localeCompare(clientName(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, names]);

  const visible = useMemo(
    () =>
      (items ?? []).filter(
        (it) =>
          (kindFilter === "all" || it.kind === kindFilter) &&
          (clientFilter === "all" || it.investor_id === clientFilter),
      ),
    [items, kindFilter, clientFilter],
  );

  const groups = useMemo<Group[]>(() => {
    const map = new Map<string, Group>();
    for (const it of visible) {
      const key = it.investor_id === null ? "unlinked" : String(it.investor_id);
      let g = map.get(key);
      if (!g) {
        g = { key, investorId: it.investor_id, name: clientName(it.investor_id), items: [] };
        map.set(key, g);
      }
      g.items.push(it);
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, names]);

  const actionableIds = (its: InboxItem[]) => its.filter(isActionable).map((i) => i.id);

  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const toggleGroup = (g: Group) =>
    setSelected((s) => {
      const ids = actionableIds(g.items);
      const allSel = ids.length > 0 && ids.every((id) => s.has(id));
      const n = new Set(s);
      for (const id of ids) {
        if (allSel) n.delete(id);
        else n.add(id);
      }
      return n;
    });

  async function sync() {
    setBusy("sync");
    setError(null);
    setNotice(null);
    qpRail.open();
    try {
      await quantfolio.inboxSync();
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function run(item: InboxItem, action: InboxAction) {
    const opts: { reason?: string; amount?: number } = {};
    if (action === "partial") {
      const a = window.prompt("Executed amount for this partial fill:");
      if (a === null) return;
      opts.amount = Number(a);
    } else if (action === "reject" || action === "cancel") {
      const r = window.prompt(
        action === "reject" ? "Reason for rejection:" : "Reason for cancellation (optional):",
      );
      if (r === null) return;
      opts.reason = r || undefined;
    }
    setMenu(null);
    setBusy(item.id);
    setError(null);
    setNotice(null);
    qpRail.open();
    try {
      await quantfolio.inboxProcess(item.id, action, opts);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function bulk(action: InboxAction) {
    const ids = [...selected];
    if (ids.length === 0) return;
    let reason: string | undefined;
    if (action === "reject" || action === "cancel") {
      const r = window.prompt(
        action === "reject"
          ? `Reason for rejecting ${ids.length} item(s):`
          : `Reason for cancelling ${ids.length} item(s) (optional):`,
      );
      if (r === null) return;
      reason = r || undefined;
    }
    setBusy("bulk");
    setError(null);
    setNotice(null);
    qpRail.open();
    try {
      const res = await quantfolio.inboxProcessBulk(ids, action, { reason });
      const parts = [`${res.processed} processed`];
      if (res.skipped) parts.push(`${res.skipped} skipped`);
      if (res.failed) parts.push(`${res.failed} failed`);
      setNotice(parts.join(" · "));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function dismiss(ids: number[]) {
    if (ids.length === 0) return;
    if (
      ids.length > 1 &&
      !window.confirm(
        `Dismiss ${ids.length} item(s)? They're hidden from the inbox (Quantfolio is not notified) and can be restored from the All view.`,
      )
    )
      return;
    setBusy("bulk");
    setError(null);
    setNotice(null);
    try {
      const res = await quantfolio.inboxDismiss(ids);
      setNotice(`${res.dismissed} dismissed`);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function restore(ids: number[]) {
    if (ids.length === 0) return;
    setBusy("bulk");
    setError(null);
    setNotice(null);
    try {
      const res = await quantfolio.inboxRestore(ids);
      setNotice(`${res.restored} restored`);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const selectedCount = selected.size;

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Quantfolio</span>
          <h1>Inbox</h1>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={sync}
          disabled={busy === "sync"}
        >
          {busy === "sync" ? "Syncing…" : "Sync from Quantfolio"}
        </button>
      </header>

      <p className="muted">
        Order, account-opening and savings-agreement requests originating in
        Quantfolio, grouped by client. Processing opens a real Andersen account
        with positions built from the orders, then writes the result back
        (account → synced, orders → executed). Posting a savings plan sets the
        agreement up at Andersen and confirms it against Quantfolio's placeholder.
      </p>

      <div className="inbox-filters">
        <div className="inbox-filter-group">
          <span className="inbox-filter-label">Status</span>
          <div className="seg">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={statusFilter === f.key ? "seg-option active" : "seg-option"}
                onClick={() => setStatusFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="inbox-filter-group">
          <span className="inbox-filter-label">Kind</span>
          <div className="seg">
            {KIND_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={kindFilter === f.key ? "seg-option active" : "seg-option"}
                onClick={() => setKindFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="inbox-filter-group inbox-filter-client">
          <span className="inbox-filter-label">Client</span>
          <select
            className="inbox-client-select"
            value={clientFilter === "all" ? "all" : String(clientFilter)}
            onChange={(e) =>
              setClientFilter(e.target.value === "all" ? "all" : Number(e.target.value))
            }
          >
            <option value="all">All clients</option>
            {clientOptions.map((id) => (
              <option key={id} value={id}>
                {clientName(id)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {notice && <div className="banner-success">{notice}</div>}
      {error && <div className="banner-error">{error}</div>}

      {selectedCount > 0 && (
        <div className="inbox-bulk-bar">
          <span className="inbox-bulk-count">
            <strong>{selectedCount}</strong> selected
          </span>
          <div className="inbox-bulk-actions">
            <button type="button" className="btn btn-primary btn-sm" disabled={busy === "bulk"} onClick={() => bulk("execute")}>
              Execute orders
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy === "bulk"} onClick={() => bulk("open")}>
              Open accounts
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy === "bulk"} onClick={() => bulk("post")}>
              Post savings plans
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy === "bulk"} onClick={() => bulk("cancel")}>
              Cancel orders
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy === "bulk"} onClick={() => bulk("reject")}>
              Reject
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy === "bulk"} onClick={() => dismiss([...selected])}>
              Dismiss
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        </div>
      )}

      {items === null ? (
        <div className="muted">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="empty">
          <p>Nothing in the inbox {statusFilter === "new" ? "to process" : "here"}.</p>
          <button type="button" className="btn btn-ghost" onClick={sync}>
            Sync from Quantfolio
          </button>
        </div>
      ) : (
        groups.map((g) => {
          const ids = actionableIds(g.items);
          const allSel = ids.length > 0 && ids.every((id) => selected.has(id));
          const someSel = ids.some((id) => selected.has(id));
          return (
            <section key={g.key} className="inbox-group">
              <div className="inbox-group-head">
                {ids.length > 0 && (
                  <input
                    type="checkbox"
                    className="inbox-check"
                    aria-label={`Select all for ${g.name}`}
                    checked={allSel}
                    ref={(el) => {
                      if (el) el.indeterminate = someSel && !allSel;
                    }}
                    onChange={() => toggleGroup(g)}
                  />
                )}
                <h2 className="inbox-group-title">
                  {g.investorId ? (
                    <Link to={`/app/crm/${g.investorId}`}>{g.name}</Link>
                  ) : (
                    g.name
                  )}
                </h2>
                <span className="muted inbox-group-count">
                  {g.items.length} item{g.items.length === 1 ? "" : "s"}
                </span>
              </div>

              <ul className="inbox-list">
                {g.items.map((it) => (
                  <li key={it.id} className="inbox-item">
                    <span className="inbox-check-cell">
                      {isActionable(it) && (
                        <input
                          type="checkbox"
                          className="inbox-check"
                          aria-label={`Select item ${it.id}`}
                          checked={selected.has(it.id)}
                          onChange={() => toggle(it.id)}
                        />
                      )}
                    </span>
                    <span className={`pill inbox-kind-${it.kind}`}>{KIND_LABEL[it.kind]}</span>
                    <div className="inbox-main">
                      <div className="inbox-summary">{it.summary || it.qp_id}</div>
                      <p className="muted inbox-meta">
                        {formatDateTime(it.received_at)}
                        {it.andersen_ref && ` · Andersen ref ${it.andersen_ref}`}
                      </p>
                      {it.error && <div className="banner-error inbox-error">{it.error}</div>}
                    </div>
                    <span className={`pill inbox-status-${it.status}`}>{it.status}</span>
                    <div className="inbox-actions">
                      {isActionable(it) && (
                        <>
                          {it.kind === "account" ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => run(it, "open")}
                                disabled={busy === it.id}
                              >
                                {busy === it.id ? "Working…" : "Open account"}
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => run(it, "reject")}
                                disabled={busy === it.id}
                              >
                                Reject
                              </button>
                            </>
                          ) : it.kind === "savings_plan" ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => run(it, "post")}
                                disabled={busy === it.id}
                                title="Set the agreement up at Andersen and confirm it in Quantfolio"
                              >
                                {busy === it.id ? "Posting…" : "Post savings plan"}
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => run(it, "reject")}
                                disabled={busy === it.id}
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <div className="qp-picker-wrap">
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => setMenu(menu === it.id ? null : it.id)}
                                disabled={busy === it.id}
                                aria-expanded={menu === it.id}
                              >
                                {busy === it.id ? "Working…" : "Execute ▾"}
                              </button>
                              {menu === it.id && (
                                <div className="qp-picker">
                                  <ul>
                                    <li>
                                      <button type="button" onClick={() => run(it, "execute")}>
                                        Execute (full)
                                      </button>
                                    </li>
                                    <li>
                                      <button type="button" onClick={() => run(it, "partial")}>
                                        Partial fill…
                                      </button>
                                    </li>
                                    <li>
                                      <button type="button" onClick={() => run(it, "reject")}>
                                        Reject…
                                      </button>
                                    </li>
                                    <li>
                                      <button type="button" onClick={() => run(it, "cancel")}>
                                        Cancel…
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => dismiss([it.id])}
                            disabled={busy === it.id}
                            title="Hide from the inbox without notifying Quantfolio"
                          >
                            Dismiss
                          </button>
                        </>
                      )}
                      {it.status === "ignored" && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => restore([it.id])}
                          disabled={busy === it.id}
                        >
                          Restore
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
